"use client";

import {
    AssistantMessageContent,
    getAssistantErrorText,
    StopNoticeLine,
} from "@/components/aios-command/chat/message-renderer";
import type { StreamPhase } from "@/components/aios-command/chat/chat-types";
import type { ContentSegment, ParsedTool } from "@/components/aios-command/chat/message-parser";
import {
    isStoppedStatus,
    NO_REPLY_TEXT,
    STOP_NOTICE_TEXT,
} from "@/components/aios-command/chat/reply-notices";
import { CommandV2StreamErrorBanner } from "@/components/command-team-v2/cards/command-v2-stream-error-banner";
import type { SelectionOption, SelectionRequest } from "@/types/command-selection";

interface AssistantMessageLike {
    content: string;
    streamError?: string;
    contentSegments?: ContentSegment[];
    tools?: ParsedTool[];
    pending?: boolean;
}

interface MessageContentPanelProps extends AssistantMessageLike {
    messageId: string;
    status?: string;
    selectionDisabled?: boolean;
    onPickSelection?: (option: SelectionOption, request: SelectionRequest) => void;
    streamingPhase?: "working" | "responding";
    /** Latest `status` phase of the streaming reply; labels the live activity line. */
    phase?: StreamPhase;
}

export function hasVisibleAssistantChunk(message: AssistantMessageLike): boolean {
    if (message.content.trim()) return true;
    if ((message.tools?.length ?? 0) > 0) return true;
    if (getAssistantErrorText(message.contentSegments, message.streamError)) return true;
    return (message.contentSegments ?? []).some((segment) => {
        if (segment.type === "text") return Boolean(segment.content?.trim());
        if (segment.type === "tool") return Boolean(segment.tool);
        if (segment.type === "selection") return Boolean(segment.selection);
        if (segment.type === "error") return Boolean(segment.content?.trim());
        return false;
    });
}

export function hasAssistantMessageBody(message: AssistantMessageLike): boolean {
    return Boolean(message.pending || hasVisibleAssistantChunk(message));
}

export function MessageContentPanel({
    content,
    streamError,
    contentSegments,
    tools,
    isStreaming,
    messageId,
    status,
    selectionDisabled,
    onPickSelection,
    phase,
}: MessageContentPanelProps & { isStreaming?: boolean }) {
    if (!hasAssistantMessageBody({ content, streamError, contentSegments, tools, pending: isStreaming })) {
        // A finished reply with nothing in it still says what happened.
        if (status === "GENERATING") return null;
        return isStoppedStatus(status) ? (
            <StopNoticeLine text={STOP_NOTICE_TEXT} />
        ) : (
            <CommandV2StreamErrorBanner text={NO_REPLY_TEXT} />
        );
    }

    // The end message (an error, or the stop notice) renders where it happened in the reply, after
    // the text and steps that came before it — not above them.
    return (
        <div className="w-full space-y-3 text-sm leading-relaxed text-foreground/90">
            <AssistantMessageContent
                content={content}
                contentSegments={contentSegments}
                streamError={streamError}
                tools={tools}
                isStreaming={isStreaming}
                messageId={messageId}
                status={status}
                selectionDisabled={selectionDisabled}
                onPickSelection={onPickSelection}
                phase={phase}
            />
        </div>
    );
}
